import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiArrowRight } from '@mdi/js';
import { GlassCard } from '../ui/GlassCard';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Section, SectionHeader } from '../ui/Section';
import { Service, services } from '../../data/services';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-6);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(GlassCard)`
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-4);
  color: white;
`;

const ServiceTitle = styled.h3`
  font-size: var(--text-xl);
  margin-bottom: var(--space-2);
`;

const ServiceTagline = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
  margin-bottom: var(--space-4);
`;

const ServiceDescription = styled.p`
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6);
  flex: 1;
`;

const FeatureList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const ViewAllWrapper = styled.div`
  text-align: center;
  margin-top: var(--space-12);
`;

interface ServicesGridProps {
  serviceList?: Service[];
  showHeader?: boolean;
  showViewAll?: boolean;
  limit?: number;
}

export function ServicesGrid({
  serviceList = services,
  showHeader = true,
  showViewAll = true,
  limit,
}: ServicesGridProps) {
  const displayServices = limit ? serviceList.slice(0, limit) : serviceList;

  return (
    <Section background="secondary">
      {showHeader && (
        <SectionHeader
          title="What We Do"
          subtitle="From concept to launch, we provide end-to-end development services tailored to your needs."
          centered
        />
      )}

      <Grid>
        {displayServices.map(service => (
          <ServiceCard key={service.id} hover>
            <IconWrapper>
              <Icon path={service.icon} size={1.2} />
            </IconWrapper>

            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceTagline>{service.tagline}</ServiceTagline>
            <ServiceDescription>{service.description}</ServiceDescription>

            <FeatureList>
              {service.features.slice(0, 4).map(feature => (
                <Badge key={feature} size="sm" variant="default">
                  {feature}
                </Badge>
              ))}
            </FeatureList>
          </ServiceCard>
        ))}
      </Grid>

      {showViewAll && (
        <ViewAllWrapper>
          <Button as={Link} to="/services" variant="secondary">
            Learn More About Our Services
            <Icon path={mdiArrowRight} size={0.8} />
          </Button>
        </ViewAllWrapper>
      )}
    </Section>
  );
}
